/* ══════════════════════════════════════════════════════════════
   Socle visuel commun — refonte du 2026-08-26
   ══════════════════════════════════════════════════════════════

   Avant : chaque écran redéfinissait ses propres couleurs et tailles
   dans un objet `styles` local. Quatre fichiers, quatre bleus, trois
   gris de texte, des rayons de 6 à 20 px selon l'humeur. Le résultat
   n'était pas seulement disparate : il empêchait de LIRE vite, parce
   que rien ne voulait dire la même chose d'un écran à l'autre.

   Règle retenue avec l'utilisateur (2026-08-26) : la priorité va à la
   clarté de l'information et à l'usage opérationnel. Le style n'est
   pas décoratif ici, il est au service de trois questions qu'un
   responsable se pose en régie, souvent dans l'urgence :
       — est-ce que tout va bien ?
       — sinon, OÙ est le problème ?
       — quel identifiant dois-je relever pour agir ?

   D'où les partis pris ci-dessous :

   COULEUR RARE. Le fond est blanc cassé, le texte presque noir, et
   TOUT le reste est gris. La couleur est réservée aux états qui
   demandent une décision (alerte, attente, succès). Quand cinq
   badges colorés coexistent, aucun ne se voit ; quand un seul est
   coloré, il saute aux yeux.

   PAS D'OMBRES. Les cartes sont séparées par une bordure d'un pixel.
   Une ombre portée simule une profondeur qui n'existe pas et brouille
   les limites entre les blocs sur un écran de tablette bon marché.

   CHIFFRES TABULAIRES. Tous les nombres sont en chasse fixe
   (`tabular-nums`) : une colonne de compteurs reste alignée et un
   chiffre qui change ne fait plus « sauter » la ligne — on relit
   sinon la valeur à chaque rafraîchissement (le dashboard se recharge
   toutes les 5 s).

   MONOSPACE POUR LES IDENTIFIANTS. Un EPC ou un UUID se compare
   caractère par caractère, souvent contre une étiquette physique ou
   un journal. En chasse fixe, deux identifiants voisins s'alignent et
   la différence saute aux yeux — c'est exactement ce qui nous a fait
   perdre du temps sur les puces `…F73E` / `…042D`.

   PAS D'EMOJI dans la navigation ni les libellés d'état. Ils
   remplaçaient un mot par un pictogramme ambigu, et donnaient à
   l'outil un registre de jouet. Les emoji restent admis là où ils
   sont un vrai raccourci visuel unique (rien pour l'instant).

   POLICE SYSTÈME, jamais une police distante : l'outil tourne en
   festival sur une connexion incertaine, et une police qui ne charge
   pas décale toute la mise en page au pire moment.
   ══════════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════
   DEUX PALETTES, UN SEUL JEU DE NOMS — thème sombre du 01/09/2026
   ══════════════════════════════════════════════════════════════

   Les couleurs ne sont plus des valeurs mais des VARIABLES CSS. Chaque
   entrée de `couleurs` vaut `var(--jums-nom)`, et les deux palettes
   ci-dessous donnent la valeur de ces variables selon le thème.

   Pourquoi ce détour : les objets de style de la console sont
   construits UNE fois au chargement du module. Avec des valeurs
   figées, changer de thème aurait exigé de tout reconstruire et de
   re-rendre chaque écran. Avec des variables, le navigateur résout la
   couleur à l'affichage : basculer l'attribut `data-theme` sur la page
   suffit, sans qu'une seule ligne de React ne s'exécute.

   La palette sombre n'est PAS la claire inversée. Un blanc pur sur
   noir pur brûle les yeux en régie de nuit : le fond est un gris très
   sombre légèrement bleuté, le texte un blanc cassé. Les couleurs
   d'état sont éclaircies, sans quoi un vert foncé sur fond sombre
   devient illisible ; leurs fonds « doux » deviennent des teintes
   sourdes plutôt que des pastels, qui feraient des taches lumineuses. */

const PALETTE_CLAIRE = {
  // ── Fonds, du plus en retrait au plus en avant ──
  fond: '#F7F8F9',          // l'arrière-plan de l'application
  surface: '#FFFFFF',       // cartes, tableaux, modales
  surfaceDouce: '#F2F3F5',  // lignes alternées, barres de progression vides
  surfaceAppui: '#EAECEF',  // survol, état pressé

  // ── Bordures : la seule façon de séparer deux blocs ──
  bord: '#E3E5E9',
  bordFort: '#CDD1D8',      // séparateur de tableau, champ au focus

  // ── Texte, trois niveaux et pas un de plus ──
  encre: '#14161A',         // titres, valeurs, tout ce qui se lit
  encreDouce: '#5C6470',    // libellés, unités, contexte
  encreFaible: '#8B929D',   // mentions accessoires, valeurs absentes

  // ── Accent : UNE couleur, pour l'élément actif et les actions ──
  accent: '#2C6BED',
  accentAppui: '#1F55C8',
  accentDoux: '#EBF1FE',
  surAccent: '#FFFFFF',     // ce qui s'écrit SUR l'accent

  // ── États. Chacun a son fond très pâle et son texte foncé :
  //    lisible, mais sans jamais crier. Le rouge est le seul qui a le
  //    droit d'attirer l'œil de loin, parce qu'il coûte de l'argent.
  succes: '#15803D',
  succesDoux: '#EAF7EE',
  alerte: '#A15C07',
  alerteDoux: '#FDF4E7',
  danger: '#C2261B',
  dangerDoux: '#FDEEEC',
  neutre: '#5C6470',
  neutreDoux: '#F0F1F3',

  // ── Voiles et ombres, seuls endroits où une transparence sert ──
  voile: 'rgba(8, 9, 11, 0.4)',        // fond derrière une modale
  ombre: '0 1px 2px rgba(20, 22, 26, .06)',
  ombreSurvol: '0 6px 20px rgba(20, 22, 26, .09)',

  // ── L'infobulle : un calque qui doit se détacher de la page ──
  //    En clair, elle est sombre : l'inversion de contraste dit qu'elle
  //    flotte, sans recourir à une ombre portée.
  bulle: '#1D2026',
  surBulle: '#EDEFF2',
};

const PALETTE_SOMBRE = {
  fond: '#0E1013',
  surface: '#16191E',
  surfaceDouce: '#1C2027',
  surfaceAppui: '#252A32',

  bord: '#272C34',
  bordFort: '#3A414B',

  encre: '#E8EAED',
  encreDouce: '#A0A8B4',
  encreFaible: '#6F7885',

  // Bleu éclairci : le #2C6BED de jour perd tout contraste sur fond
  // sombre. Le fond « doux » devient un bleu sourd, pas un pastel.
  accent: '#4D8BFF',
  accentAppui: '#6FA2FF',
  accentDoux: '#16233A',
  // Sur ce bleu éclairci, un texte blanc passe mal : on écrit sombre.
  surAccent: '#0E1013',

  succes: '#4ADE80',
  succesDoux: '#122A1C',
  alerte: '#F0B429',
  alerteDoux: '#2C2210',
  danger: '#FF6B5E',
  dangerDoux: '#2E1614',
  neutre: '#A0A8B4',
  neutreDoux: '#22262D',

  voile: 'rgba(0, 0, 0, 0.62)',
  ombre: '0 1px 2px rgba(0, 0, 0, .4)',
  ombreSurvol: '0 6px 20px rgba(0, 0, 0, .5)',

  // En sombre, l'inversion ne peut PAS être reprise telle quelle : une
  // infobulle claire serait un éclair blanc en pleine nuit, exactement
  // ce que ce thème existe pour éviter. Elle se détache donc en étant
  // plus CLAIRE que la page, mais à peine — un cran au-dessus.
  bulle: '#272D36',
  surBulle: '#E8EAED',
};

/* `couleurs` garde exactement les mêmes noms qu'avant : aucun écran
   n'a besoin de savoir qu'il existe deux palettes. */
export const couleurs = Object.fromEntries(
  Object.keys(PALETTE_CLAIRE).map(nom => [nom, `var(--jums-${nom})`])
);

// Échelle d'espacement en multiples de 4 : les alignements tombent
// juste sans avoir à y penser, et rien ne dérive à 13 ou 17 px.
export const espace = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 };

// Rayons discrets. Le 999 est réservé aux vraies pastilles rondes
// (points d'état), plus jamais aux boutons ni aux badges : un bouton
// entièrement arrondi appartient au registre du jeu mobile.
export const rayon = { sm: 4, md: 6, lg: 8, xl: 12, rond: 999 };

export const police = {
  ui: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
};

/* Échelle typographique — six tailles, pas davantage. Chaque niveau
   correspond à un rôle précis ; s'il faut un septième, c'est que la
   hiérarchie de la page est à revoir, pas la police. */
export const texte = {
  // Le grand chiffre d'un indicateur. Poids 600 et non 700 : à cette
  // taille le gras lourd bave sur les écrans non calibrés.
  metrique: { fontSize: 30, fontWeight: 600, letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums', color: couleurs.encre, margin: 0 },
  titre:    { fontSize: 21, fontWeight: 600, letterSpacing: '-0.015em',
              color: couleurs.encre, margin: 0 },
  section:  { fontSize: 15, fontWeight: 600, color: couleurs.encre, margin: 0 },
  corps:    { fontSize: 13.5, color: couleurs.encre },
  aide:     { fontSize: 12.5, color: couleurs.encreDouce, lineHeight: 1.55 },
  // Libellé d'indicateur ou d'en-tête de tableau : petites capitales
  // espacées. C'est ce qui permet de distinguer une ÉTIQUETTE d'une
  // VALEUR sans recourir à une couleur supplémentaire.
  etiquette: { fontSize: 10.5, fontWeight: 600, letterSpacing: '0.06em',
               textTransform: 'uppercase', color: couleurs.encreDouce },
  mono:     { fontFamily: police.mono, fontSize: 12, color: couleurs.encre },
};

/* ── Primitives partagées ────────────────────────────────────── */

export const carte = {
  background: couleurs.surface,
  border: `1px solid ${couleurs.bord}`,
  borderRadius: rayon.xl,
  padding: espace.xl,
};

export const champ = {
  padding: '9px 11px',
  border: `1px solid ${couleurs.bordFort}`,
  borderRadius: rayon.md,
  fontSize: 13.5,
  fontFamily: police.ui,
  color: couleurs.encre,
  background: couleurs.surface,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

/* Boutons : trois variantes seulement.
     primaire  — l'action attendue de l'écran, une seule par vue ;
     discret   — tout le reste, y compris les actions fréquentes ;
     danger    — ce qui engage de l'argent ou détruit une donnée.
   Un bouton n'est jamais vert : le vert dit « c'est fait », pas
   « cliquez ici », et les deux ne doivent pas se confondre. */
export function bouton(variante = 'discret', taille = 'md') {
  const base = {
    fontFamily: police.ui,
    fontWeight: 500,
    borderRadius: rayon.md,
    cursor: 'pointer',
    lineHeight: 1.2,
    transition: 'background 120ms ease, border-color 120ms ease',
    ...(taille === 'sm'
      ? { fontSize: 12.5, padding: '6px 10px' }
      : { fontSize: 13.5, padding: '9px 15px' }),
  };
  const variantes = {
    primaire: { background: couleurs.accent, color: '#FFFFFF',
                border: `1px solid ${couleurs.accent}` },
    discret:  { background: couleurs.surface, color: couleurs.encre,
                border: `1px solid ${couleurs.bordFort}` },
    danger:   { background: couleurs.surface, color: couleurs.danger,
                border: `1px solid ${couleurs.danger}` },
  };
  return { ...base, ...(variantes[variante] || variantes.discret) };
}

/* Badge d'état. `ton` vaut succes | alerte | danger | accent | neutre.
   Rectangle à coins légèrement adoucis, jamais une pastille ronde :
   on lit un mot, pas une décoration. */
export function badge(ton = 'neutre') {
  const tons = {
    succes: [couleurs.succesDoux, couleurs.succes],
    alerte: [couleurs.alerteDoux, couleurs.alerte],
    danger: [couleurs.dangerDoux, couleurs.danger],
    accent: [couleurs.accentDoux, couleurs.accent],
    neutre: [couleurs.neutreDoux, couleurs.neutre],
  };
  const [fond, encre] = tons[ton] || tons.neutre;
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    background: fond, color: encre,
    padding: '2px 7px', borderRadius: rayon.sm,
    fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap',
  };
}

/* Tableaux — denses et lisibles. L'en-tête reste collé en haut
   (`position: sticky`) : sur 50 transactions, on perd sinon le nom
   des colonnes dès la première molette. */
export const tableau = {
  table: { width: '100%', borderCollapse: 'collapse', fontFamily: police.ui },
  th: {
    ...texte.etiquette,
    textAlign: 'left', padding: '9px 12px',
    borderBottom: `1px solid ${couleurs.bordFort}`,
    background: couleurs.surface,
    position: 'sticky', top: 0, zIndex: 1,
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '9px 12px', fontSize: 13,
    color: couleurs.encre,
    borderBottom: `1px solid ${couleurs.bord}`,
    verticalAlign: 'middle',
  },
  // Colonne d'identifiant : chasse fixe, jamais coupée sur deux lignes.
  tdMono: {
    padding: '9px 12px', fontSize: 12, fontFamily: police.mono,
    color: couleurs.encre, borderBottom: `1px solid ${couleurs.bord}`,
    whiteSpace: 'nowrap',
  },
  // Colonne de nombre : alignée à droite, chasse tabulaire.
  tdNombre: {
    padding: '9px 12px', fontSize: 13, textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', color: couleurs.encre,
    borderBottom: `1px solid ${couleurs.bord}`,
  },
};

/* Bandeau d'information en tête de section. `ton` comme pour badge().
   Bordure gauche épaisse plutôt qu'un aplat : le message reste lisible
   et la couleur se voit du coin de l'œil sans saturer la page. */
export function bandeau(ton = 'neutre') {
  const tons = {
    succes: [couleurs.succesDoux, couleurs.succes],
    alerte: [couleurs.alerteDoux, couleurs.alerte],
    danger: [couleurs.dangerDoux, couleurs.danger],
    accent: [couleurs.accentDoux, couleurs.accent],
    neutre: [couleurs.neutreDoux, couleurs.encreDouce],
  };
  const [fond, encre] = tons[ton] || tons.neutre;
  return {
    background: fond, color: encre,
    borderLeft: `2px solid ${encre}`,
    borderRadius: `0 ${rayon.md}px ${rayon.md}px 0`,
    padding: '10px 14px', fontSize: 13, lineHeight: 1.5,
  };
}

/* Point d'état — le seul rond qui subsiste. Sert à dire « ce matériel
   répond / ne répond pas » d'un coup d'œil, à côté d'un nom. */
export function pastille(ton = 'neutre') {
  const tons = {
    succes: couleurs.succes, alerte: couleurs.alerte,
    danger: couleurs.danger, accent: couleurs.accent, neutre: couleurs.encreFaible,
  };
  return {
    display: 'inline-block', width: 6, height: 6, borderRadius: rayon.rond,
    background: tons[ton] || tons.neutre, flex: 'none',
  };
}

/* ══════════════════════════════════════════════════════════════
   Bascule clair / sombre
   ══════════════════════════════════════════════════════════════

   Trois règles, chacune apprise d'un défaut classique :

   1. Le thème est posé sur <html>, PAS dans un état React. Les objets
      de style sont figés au chargement du module ; seul le navigateur
      peut re-résoudre les couleurs sans re-rendre, et il ne le fait
      qu'à travers les variables CSS.

   2. Le choix est appliqué AVANT le premier affichage (voir index.js),
      sinon la page s'affiche en clair puis clignote en sombre — un
      éclair blanc en pleine nuit, exactement ce qu'on cherche à
      éviter.

   3. `color-scheme` accompagne le thème : sans lui, les ascenseurs,
      les menus déroulants natifs et les sélecteurs de date restent
      blancs au milieu d'une page sombre. */

export const THEME_CLAIR = 'clair';
export const THEME_SOMBRE = 'sombre';
const CLE_STOCKAGE = 'jums-theme';

function bloc(selecteur, palette, schema) {
  const lignes = Object.entries(palette)
    .map(([nom, valeur]) => `  --jums-${nom}: ${valeur};`)
    .join('\n');
  return `${selecteur} {\n  color-scheme: ${schema};\n${lignes}\n}`;
}

export const CSS_THEMES = [
  bloc(':root', PALETTE_CLAIRE, 'light'),
  bloc('[data-theme="sombre"]', PALETTE_SOMBRE, 'dark'),
  /* Le fond de la page elle-même : sans cette règle, la zone au-delà
     du contenu (rebond de défilement, page courte) reste blanche. */
  'html, body { background: var(--jums-fond); }',
  /* La transition ne porte QUE sur la bascule manuelle. Sans le
     `[data-transition]`, chaque changement de couleur d'un badge
     traînerait sur 180 ms — un tableau qui se rafraîchit toutes les
     5 s deviendrait mou. */
  '[data-transition] *, [data-transition] { transition: background-color 180ms ease, '
    + 'border-color 180ms ease, color 180ms ease !important; }',
].join('\n\n');

export function themeEnregistre() {
  /* Un navigateur en navigation privée, ou avec le stockage bloqué,
     lève à la simple lecture. Le thème n'est pas une donnée critique :
     on retombe silencieusement sur la préférence du système. */
  try {
    const v = window.localStorage.getItem(CLE_STOCKAGE);
    if (v === THEME_CLAIR || v === THEME_SOMBRE) return v;
  } catch (e) { /* stockage indisponible */ }
  try {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) return THEME_SOMBRE;
  } catch (e) { /* matchMedia indisponible */ }
  return THEME_CLAIR;
}

export function appliquerTheme(nom, animer = false) {
  const theme = nom === THEME_SOMBRE ? THEME_SOMBRE : THEME_CLAIR;
  const racine = document.documentElement;
  if (animer) {
    racine.setAttribute('data-transition', '');
    window.setTimeout(() => racine.removeAttribute('data-transition'), 220);
  }
  if (theme === THEME_SOMBRE) racine.setAttribute('data-theme', THEME_SOMBRE);
  else racine.removeAttribute('data-theme');
  try { window.localStorage.setItem(CLE_STOCKAGE, theme); }
  catch (e) { /* le thème vaudra pour cette session seulement */ }
  return theme;
}

/* Pose la feuille de styles une fois pour toutes. Écrite depuis les
   palettes JavaScript plutôt que dans un fichier .css à part : un seul
   endroit définit les couleurs, et un nom de jeton renommé ne peut pas
   se désynchroniser en silence. */
export function installerThemes() {
  if (document.getElementById('jums-themes')) return;
  const style = document.createElement('style');
  style.id = 'jums-themes';
  style.textContent = CSS_THEMES;
  document.head.appendChild(style);
}
